"use client";

import type React from "react";
import type { LocationItem, LocationResponse } from "@/features/profile/tutor/types/Location.ts";

import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useDebouncedCallback } from "use-debounce";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import Cropper from "react-easy-crop";

import { getHeaderSchema, type HeaderFormData } from "../../../schemas/profileEditSchemas.ts";
import { useProfileDataStore } from "../../../store/useProfileDataStore";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InformationTooltip } from "@/components/InformationTooltip";
import { useProfileEditFooterStore } from "@/features/profile/tutor/store/useProfileEditFooterStore";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { editTutorProfile } from "@/features/profile/tutor/api/editProfile.ts";
// Removed DialogHeader/DialogTitle to align with card-based layout inside the section
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { cropImage, getImageDimensions, readFileAsDataURL } from "@/utils/imageCropper";

type EditStep = "overview" | "avatar" | "background" | "location";

interface HeaderEditProps {
  onSave: () => void;
}

export function HeaderEdit({ onSave }: HeaderEditProps) {
  const { profileData, setToReload } = useProfileDataStore();
  const data = profileData?.header_section;

  // Step management
  const [currentStep, setCurrentStep] = useState<EditStep>("overview");

  // Location states
  const [locationSearch, setLocationSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [isCountryOnly, setIsCountryOnly] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<LocationItem | null>(null);
  const [pendingClear, setPendingClear] = useState(false);

  // Image cropping states
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Preview states - these persist across steps
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null);

  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const clearFooter = useProfileEditFooterStore((s) => s.clearFooter);

  useEffect(() => {
    clearFooter();
  }, [clearFooter]);

  const form = useForm<HeaderFormData>({
    resolver: zodResolver(getHeaderSchema(t)),
    defaultValues: {
      name: data?.name || "",
      headline: data?.headline || "",
      current_location_name: data?.current_location_name || "",
      avatar: "",
      background: "",
    },
  });
  const {
    formState: { isDirty },
  } = form;

  // Cropping handlers
  const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const file = files[0];

    if (file && file.type.startsWith("image/")) {
      processFile(file, currentStep as "avatar" | "background");
    } else {
      toast.error(t("common:error"), {
        description: t("profile:errors.invalidFileType"),
      });
    }
  };

  const processFile = async (file: File, _type: "avatar" | "background") => {
    try {
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t("common:error"), {
          description: t("profile:errors.fileSizeLimit"),
        });

        return;
      }

      // Validate file type
      if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
        toast.error(t("common:error"), {
          description: t("profile:errors.invalidFormat"),
        });

        return;
      }

      // Validate file dimensions
      const dimensions = await getImageDimensions(file);

      if (dimensions.width < 50 || dimensions.height < 50) {
        toast.error(t("common:error"), {
          description: t("profile:errors.imageTooSmall"),
        });

        return;
      }

      // Read file and set for cropping
      const dataUrl = await readFileAsDataURL(file);

      setSelectedImage(dataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      toast.error(t("common:error"), {
        description: t("profile:errors.imageLoadFailed"),
      });
    }
  };

  const openFileDialog = (type: "avatar" | "background") => {
    const input = document.createElement("input");

    input.type = "file";
    input.accept = "image/jpeg,image/jpg,image/png,image/gif,image/webp";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];

      if (file) processFile(file, type);
    };
    input.click();
  };

  const handleImageConfirm = async () => {
    if (!selectedImage || !croppedAreaPixels) return;

    try {
      // Configure cropping options based on image type
      const cropOptions =
        currentStep === "avatar"
          ? {
              maxSizeMB: 2,
              minDimensions: 50,
              maxDimensions: 2048,
              quality: 0.95,
            }
          : {
              maxSizeMB: 2,
              minDimensions: 50,
              maxDimensions: 2048,
              quality: 0.95,
              targetWidth: 1200, // Backend operates on 1200x300px
              targetHeight: 300, // Maintain 4:1 ratio
            };

      // Crop the image
      const croppedFile = await cropImage(selectedImage, croppedAreaPixels, cropOptions);

      // Set form value and preview
      form.setValue(currentStep as "avatar" | "background", croppedFile, { shouldDirty: true });

      // Create preview URL from the cropped file
      const croppedPreviewUrl = URL.createObjectURL(croppedFile);

      if (currentStep === "avatar") {
        // Clean up old preview URL
        if (avatarPreview && avatarPreview.startsWith("blob:")) {
          URL.revokeObjectURL(avatarPreview);
        }
        setAvatarPreview(croppedPreviewUrl);
      } else {
        // Clean up old preview URL
        if (backgroundPreview && backgroundPreview.startsWith("blob:")) {
          URL.revokeObjectURL(backgroundPreview);
        }
        setBackgroundPreview(croppedPreviewUrl);
      }

      // Reset cropping state and go back to overview
      setSelectedImage("");
      setCroppedAreaPixels(null);
      setCurrentStep("overview");
    } catch (error: any) {
      console.log(error);
      toast.error(t("common:error"), {
        description: error.message?.includes("file size")
          ? t("profile:errors.croppedImageTooLarge")
          : t("profile:errors.imageProcessingFailed"),
      });
    }
  };

  const handleSubmit = async () => {
    const isValid = await form.trigger();

    try {
      if (profileData && isValid) {
        const formData = new FormData();
        const values = form.getValues();
        const originalData = data;

        // Always add name field, and only add other fields that have actually changed
        Object.keys(values).forEach((key) => {
          if (key === "current_location_name" || key === "current_location_coords") {
            // Skip here; handle below based on pendingLocation/pendingClear
            return;
          }
          if (key === "avatar" && values[key] !== "" && values[key] instanceof File) {
            formData.append(key, values[key] as File);
          } else if (key === "background" && values[key] !== "" && values[key] instanceof File) {
            formData.append(key, values[key] as File);
          } else if (key === "name") {
            // Always include name field
            //@ts-ignore
            formData.append(key, values[key] as string);
          }

          // Handle headline separately to allow clearing with empty string
          else if (key === "headline") {
            const newHeadline = (values as any).headline ?? "";
            const originalHeadline = originalData?.headline ?? "";

            if (newHeadline !== originalHeadline) {
              // Empty string clears headline
              if (!String(newHeadline).trim()) {
                formData.append("headline", "");
              } else {
                formData.append("headline", String(newHeadline));
              }
            }
          }
        });

        // Apply pending location or clear
        if (pendingClear) {
          formData.append("current_location_name", "");
        } else if (pendingLocation) {
          const locationNameToSend = isCountryOnly
            ? pendingLocation.address.countryName
            : pendingLocation.address.label;

          formData.append("current_location_name", locationNameToSend);
          formData.append("current_location_coords[lat]", pendingLocation.position.lat.toString());
          formData.append("current_location_coords[lng]", pendingLocation.position.lng.toString());
        }

        // Only submit if there are actual changes
        if (formData.entries().next().done === false) {
          await editTutorProfile(profileData.id, "header", formData);
          setToReload(true);
          toast.success(t("common:success"), {
            description: t("common:successMessage"),
          });
          onSave();
        } else {
          // No changes made
          onSave();
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    }
  };

  const debouncedFetchLocations = useDebouncedCallback(async (query: string) => {
    try {
      const { data } = await axiosApiInstance.get<LocationResponse>(
        API_ENDPOINTS.TUTOR.UTILS.LOCATION,
        {
          params: {
            query,
            locale_code: profileData?.header_section.locale_code,
          },
        },
      );

      setLocations(data.items);
      setIsSearching(false);
    } catch (error) {
      console.error(error);
    }
  }, 1000);

  useEffect(() => {
    if (locationSearch.length >= 3) {
      setIsSearching(true);
      debouncedFetchLocations(locationSearch);
    } else {
      setLocations([]);
      setIsSearching(false);
    }
  }, [locationSearch, debouncedFetchLocations]);

  const handleLocationSelect = (location: LocationItem) => {
    // Do not update current form location yet; store selection pending save
    setPendingLocation(location);
    setPendingClear(false);
    setLocationSearch("");
  };

  const resetStep = () => {
    setSelectedImage("");
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setIsDragging(false);
    setLocationSearch("");
    setCurrentStep("overview");
    // Note: We don't clear previews here as they should persist across steps
  };

  // Cleanup preview URLs on component unmount
  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith("blob:")) {
        URL.revokeObjectURL(avatarPreview);
      }
      if (backgroundPreview && backgroundPreview.startsWith("blob:")) {
        URL.revokeObjectURL(backgroundPreview);
      }
    };
  }, [avatarPreview, backgroundPreview]);

  const renderOverviewStep = () => (
    <Form {...form}>
      <form
        className="flex h-full flex-col pb-24 sm:pb-10"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <div className="grid grid-cols-1 items-stretch gap-6 xl:grid-cols-12">
          {/* Left column: media + basic info */}
          <div className="flex h-full flex-col space-y-6 xl:col-span-7">
            {/* Profile media card */}
            <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-accent-foreground text-lg">
                    {t("profile:headerSection.cards.profileMedia.title", {
                      defaultValue: "Profile media",
                    })}
                  </CardTitle>
                  <InformationTooltip
                    description={t("profile:headerSection.tooltips.profileMedia", {
                      defaultValue:
                        "Use clear, well-lit images. A friendly avatar and a clean background make your profile more engaging.",
                    })}
                  />
                </div>
                <CardDescription className="text-muted-foreground">
                  {t("profile:headerSection.cards.profileMedia.description", {
                    defaultValue: "Update your background and avatar images",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative rounded-xl">
                  {/* background circles */}
                  <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 opacity-10" />
                  <div className="pointer-events-none absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-500 opacity-10" />

                  {/* Header Image Preview */}
                  <div className="group relative h-[220px] w-full rounded-xl">
                    <img
                      alt=""
                      className="h-full w-full rounded-xl object-cover"
                      src={backgroundPreview || data?.background_image || "/placeholder.svg"}
                    />
                    <button
                      aria-label={t("profile:headerSection.changeBackground")}
                      className="border-border/50 bg-background/80 absolute top-2 right-2 z-20 flex items-center rounded-lg border p-2 opacity-100 shadow-sm transition-opacity xl:opacity-0 xl:group-hover:opacity-100"
                      type="button"
                      onClick={() => setCurrentStep("background")}
                    >
                      <Icon height="1.125rem" icon="mdi:camera-plus" width="1.125rem" />
                    </button>

                    {/* Avatar */}
                    <div className="absolute -bottom-2 -left-1">
                      <div className="relative z-20 h-32 w-32">
                        <div className="border-primary bg-background relative h-32 w-32 overflow-hidden rounded-2xl border-4 shadow-xl">
                          <img
                            alt={data?.name}
                            className="h-32 w-32 object-cover"
                            src={avatarPreview || data?.avatar_image || "/placeholder.svg"}
                          />
                          <button
                            aria-label={t("profile:headerSection.changeAvatar")}
                            className="border-border/50 bg-background/80 absolute right-1 bottom-1 z-30 flex items-center rounded-lg border p-2 opacity-100 shadow-sm transition-opacity xl:opacity-0 xl:group-hover:opacity-100"
                            type="button"
                            onClick={() => setCurrentStep("avatar")}
                          >
                            <Icon height="1rem" icon="mdi:camera-plus" width="1rem" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Basic information card */}
            <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-accent-foreground text-lg">
                    {t("profile:headerSection.cards.basicInfo.title", {
                      defaultValue: "Basic information",
                    })}
                  </CardTitle>
                  <InformationTooltip
                    description={t("profile:headerSection.tooltips.basicInfo", {
                      defaultValue:
                        "Your name and headline appear at the top of your profile. Keep them concise and professional.",
                    })}
                  />
                </div>
                <CardDescription className="text-muted-foreground">
                  {t("profile:headerSection.cards.basicInfo.description", {
                    defaultValue: "Your display name and headline",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="pointer-events-none absolute -top-10 -right-12 h-32 w-32 rounded-full bg-gradient-to-br from-pink-400 to-fuchsia-600 opacity-10" />
                <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-10" />
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-accent-foreground flex items-center gap-2 font-medium">
                          <div className="flex items-center justify-center p-1">
                            <Icon height="1rem" icon="mdi:account" width="1rem" />
                          </div>
                          {t("profile:headerSection.name")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            className="bg-background/50 focus:bg-background/70 h-12 flex-1 rounded-xl border-none transition-all"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="headline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-accent-foreground flex items-center gap-2 font-medium">
                          <div className="flex items-center justify-center p-1">
                            <Icon height="1rem" icon="mdi:format-title" width="1rem" />
                          </div>
                          {t("profile:headerSection.headline")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            className="bg-background/50 focus:bg-background/70 h-12 flex-1 rounded-xl border-none transition-all"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column: location */}
          <div className="h-full space-y-6 xl:col-span-5">
            <Card className="bg-side-accent/80 border-border/30 relative h-full overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-accent-foreground text-lg">
                    {t("profile:headerSection.cards.location.title", { defaultValue: "Location" })}
                  </CardTitle>
                  <InformationTooltip
                    description={t("profile:headerSection.tooltips.location", {
                      defaultValue:
                        "Choose how precise your location should be. It helps students understand where you teach from.",
                    })}
                  />
                </div>
                <CardDescription className="text-muted-foreground">
                  {t("profile:headerSection.cards.location.description", {
                    defaultValue: "Set where you're based to personalize your profile",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent className="h-full p-1">
                <div className="relative h-full">
                  {/* background circle */}
                  <div className="pointer-events-none absolute -top-8 -right-10 h-24 w-24 rounded-full bg-gradient-to-br from-amber-400 to-pink-500 opacity-10" />

                  <div className="flex h-full flex-col gap-4 overflow-hidden px-4">
                    {/* Current location static card */}
                    <Card className="bg-muted/60 border-border/30">
                      <CardContent className="p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <div className="bg-background/60 flex items-center rounded-xl p-2">
                                <Icon height="1.125rem" icon="mdi:map-marker" width="1.125rem" />
                              </div>
                              <div className="text-accent-foreground font-medium">
                                {t("profile:headerSection.currentLocationCard.title", {
                                  defaultValue: "Current location",
                                })}
                              </div>
                            </div>
                            <div className="text-muted-foreground text-sm">
                              {data?.current_location_name ||
                                t("profile:headerSection.currentLocationCard.notSet", {
                                  defaultValue: "Not set",
                                })}
                            </div>
                            <div className="text-muted-foreground mt-1 text-xs">
                              {t("profile:headerSection.currentLocationCard.note", {
                                defaultValue: "You can clear or change it below.",
                              })}
                            </div>
                          </div>
                          <Button
                            className="h-8 w-full sm:w-auto"
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setPendingLocation(null);
                              setPendingClear(true);
                            }}
                          >
                            <div className="mr-1 flex items-center justify-center p-0.5">
                              <Icon height="0.9rem" icon="mdi:close-circle" width="0.9rem" />
                            </div>
                            {t("profile:headerSection.clearLocation")}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    {/* location precision options */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <motion.div
                        animate={{ scale: isCountryOnly ? 1.01 : 1 }}
                        transition={{ duration: 0.15 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <Card
                          className={`cursor-pointer transition-all ${
                            isCountryOnly
                              ? "bg-accent-foreground text-primary"
                              : "bg-accent text-muted-foreground"
                          }`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setIsCountryOnly(true)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setIsCountryOnly(true);
                            }
                          }}
                        >
                          <CardContent className="p-4">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <div className="bg-background flex items-center rounded-xl p-2">
                                  <Icon
                                    className={
                                      isCountryOnly
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground"
                                    }
                                    height="1.125rem"
                                    icon="mdi:earth"
                                    width="1.125rem"
                                  />
                                </div>
                                <div className="font-medium">
                                  {t("profile:headerSection.locationOptions.general.title", {
                                    defaultValue: "General location",
                                  })}
                                </div>
                              </div>
                              <div className="text-sm opacity-80">
                                {t("profile:headerSection.locationOptions.general.description", {
                                  defaultValue: "Display only your country or region",
                                })}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                      <motion.div
                        animate={{ scale: !isCountryOnly ? 1.01 : 1 }}
                        transition={{ duration: 0.15 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <Card
                          className={`cursor-pointer transition-all ${
                            !isCountryOnly
                              ? "bg-accent-foreground text-primary"
                              : "bg-accent text-muted-foreground"
                          }`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setIsCountryOnly(false)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setIsCountryOnly(false);
                            }
                          }}
                        >
                          <CardContent className="p-4">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <div className="bg-background flex items-center rounded-xl p-2">
                                  <Icon
                                    className={
                                      !isCountryOnly
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground"
                                    }
                                    height="1.125rem"
                                    icon="mdi:map-marker"
                                    width="1.125rem"
                                  />
                                </div>
                                <div className="font-medium">
                                  {t("profile:headerSection.locationOptions.accurate.title", {
                                    defaultValue: "Accurate location",
                                  })}
                                </div>
                              </div>
                              <div className="text-sm opacity-80">
                                {t("profile:headerSection.locationOptions.accurate.description", {
                                  defaultValue: "Display your city and country",
                                })}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </div>
                    <div className="flex gap-3">
                      <Input
                        className="bg-background/50 focus:bg-background/70 h-12 flex-1 rounded-xl border-none transition-all"
                        placeholder={t("profile:headerSection.searchLocation", {
                          type: t("profile:headerSection.cityTown"),
                        })}
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                      />
                    </div>
                    {/* Results / Pending selection area */}
                    <div className="min-h-0 flex-1">
                      {pendingLocation ? (
                        <Card className="bg-muted/60 border-border/30">
                          <CardContent className="flex items-center justify-between gap-3 p-4">
                            <div className="flex items-start gap-3">
                              <div className="bg-background/60 flex items-center rounded-xl p-2">
                                <Icon
                                  className="text-muted-foreground"
                                  height="1.125rem"
                                  icon={isCountryOnly ? "mdi:earth" : "mdi:map-marker"}
                                  width="1.125rem"
                                />
                              </div>
                              <div>
                                <div className="text-accent-foreground font-medium">
                                  {t("profile:headerSection.pendingSelection.title", {
                                    defaultValue: "Selected location (not saved)",
                                  })}
                                </div>
                                <div className="text-muted-foreground text-sm">
                                  {isCountryOnly
                                    ? pendingLocation.address.countryName
                                    : pendingLocation.address.label}
                                </div>
                              </div>
                            </div>
                            <Button
                              className="bg-muted/20 hover:bg-muted/30 border-border/50"
                              size="sm"
                              variant="outline"
                              onClick={() => setPendingLocation(null)}
                            >
                              <div className="mr-1 flex items-center justify-center p-0.5">
                                <Icon height="0.9rem" icon="mdi:close" width="0.9rem" />
                              </div>
                              {t("profile:headerSection.pendingSelection.change", {
                                defaultValue: "Change",
                              })}
                            </Button>
                          </CardContent>
                        </Card>
                      ) : locationSearch.length >= 3 ? (
                        <div className="flex h-full flex-col gap-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-accent-foreground font-medium">
                              {t("profile:headerSection.searchResults")}
                            </h4>
                            {isSearching && (
                              <div className="flex items-center justify-center p-1">
                                <Loader2 className="h-4 w-4 animate-spin" />
                              </div>
                            )}
                          </div>
                          <ScrollArea className="h-48">
                            <div className="space-y-2">
                              {locations.map((location) => (
                                <Card
                                  key={location.id}
                                  className="bg-muted border-border/30 hover:border-accent/30 hover:bg-accent/50 cursor-pointer transition-all"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleLocationSelect(location)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleLocationSelect(location);
                                    }
                                  }}
                                >
                                  <CardContent className="p-3">
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center justify-center p-1">
                                        <Icon
                                          className="text-muted-foreground"
                                          height="1rem"
                                          icon={isCountryOnly ? "mdi:earth" : "mdi:map-marker"}
                                          width="1rem"
                                        />
                                      </div>
                                      <span className="font-medium">{location.address.label}</span>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}

                              {!isSearching && locations.length === 0 && (
                                <div className="text-muted-foreground py-8 text-center">
                                  {t("profile:headerSection.noLocationsFound", {
                                    search: locationSearch,
                                  })}
                                </div>
                              )}
                            </div>
                          </ScrollArea>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-6">
          <Button
            className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
            variant="outline"
            onClick={onSave}
          >
            {t("common:cancel")}
          </Button>
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl"
            disabled={!isDirty && !pendingLocation && !pendingClear}
            type="submit"
          >
            {form.formState.isSubmitting ? t("common:saving") : t("common:save")}
          </Button>
        </div>
      </form>
    </Form>
  );

  const renderImageCropStep = () => (
    <div className="space-y-6">
      {/* Back Button */}
      <Button className="mb-4 h-auto p-2" variant="ghost" onClick={resetStep}>
        <div className="mr-2 flex items-center justify-center p-1">
          <Icon height="1.25rem" icon="mdi:arrow-left" width="1.25rem" />
        </div>
        {t("common:back")}
      </Button>

      <div className="space-y-2 text-center">
        <h3 className="text-accent-foreground text-xl font-semibold">
          {currentStep === "avatar"
            ? t("profile:headerSection.cropAvatar")
            : t("profile:headerSection.cropBackground")}
        </h3>
        <p className="text-muted-foreground text-sm">
          {currentStep === "avatar"
            ? t("profile:headerSection.cropAvatarDescription")
            : t("profile:headerSection.cropBackgroundDescription")}
        </p>
      </div>

      {!selectedImage && (
        <div className="relative">
          <div
            className={`border-input hover:bg-accent/50 relative flex min-h-64 flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed p-4 transition-colors ${
              isDragging ? "bg-accent/50 border-accent" : ""
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
              <div className="bg-background mb-4 flex size-16 shrink-0 items-center justify-center rounded-full border">
                <div className="flex items-center justify-center p-2">
                  <Icon
                    className="opacity-60"
                    height="1.5rem"
                    icon="lucide:image-up"
                    width="1.5rem"
                  />
                </div>
              </div>
              <p className="mb-2 text-lg font-medium">{t("profile:headerSection.dropImageHere")}</p>
              <p className="text-muted-foreground text-sm">
                {t("profile:headerSection.imageRequirements")}
              </p>
              <Button
                className="mt-4"
                type="button"
                onClick={() => openFileDialog(currentStep as "avatar" | "background")}
              >
                <div className="mr-2 flex items-center justify-center p-1">
                  <Icon height="1rem" icon="lucide:upload" width="1rem" />
                </div>
                {t("profile:headerSection.selectImage", { defaultValue: "Select image" })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="space-y-4">
          <div className="bg-muted/5 relative h-80 overflow-hidden rounded-xl">
            <Cropper
              aspect={currentStep === "avatar" ? 1 : 4}
              crop={crop}
              image={selectedImage}
              objectFit="contain"
              showGrid={true}
              zoom={zoom}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>

          {/* Zoom Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-accent-foreground text-sm font-medium">{t("common:zoom")}</span>
              <span className="text-muted-foreground text-xs">{Math.round(zoom * 100)}%</span>
            </div>
            <Slider
              className="w-full"
              max={3}
              min={1}
              step={0.1}
              value={[zoom]}
              onValueChange={(value) => setZoom(value[0])}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              className="bg-muted/20 hover:bg-muted/30 border-border/50 flex-1"
              variant="outline"
              onClick={() => openFileDialog(currentStep as "avatar" | "background")}
            >
              <div className="mr-2 flex items-center justify-center p-1">
                <Icon height="1rem" icon="lucide:upload" width="1rem" />
              </div>
              {t("profile:headerSection.chooseDifferentImage")}
            </Button>
            <Button
              className="bg-accent-foreground/90 hover:bg-accent-foreground text-background flex-1"
              disabled={!croppedAreaPixels}
              onClick={handleImageConfirm}
            >
              <div className="mr-2 flex items-center justify-center p-1">
                <Icon height="1rem" icon="lucide:check" width="1rem" />
              </div>
              {t("profile:headerSection.confirmCrop")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Old renderLocationStep kept temporarily during redesign; not used anymore

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <AnimatePresence mode="wait">
        {currentStep === "overview" && (
          <motion.div
            key="overview"
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            initial={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            {renderOverviewStep()}
          </motion.div>
        )}

        {(currentStep === "avatar" || currentStep === "background") && (
          <motion.div
            key="image-crop"
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            initial={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderImageCropStep()}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
